/**
 * useDeleteRole Hook
 *
 * React Query mutation hook for deleting roles.
 * Handles cache invalidation, error handling, and confirmation flows.
 *
 * @module domains/personnel/presentation/hooks/useDeleteRole
 */

import { useMutation, useQueryClient, UseMutationResult } from 'react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@parrot-software/design-system';

import { getRoleUseCases } from '@personnel/composition';
import { ROLES_QUERY_KEYS } from './useRoleList';
import { useApiErrorHandler } from './useApiErrorHandler';

/**
 * Hook options for configuring delete behavior
 */
export interface UseDeleteRoleOptions {
  /** Callback executed on successful deletion */
  onSuccess?: (uuid: string) => void;
  /** Callback executed on error */
  onError?: (error: Error, uuid: string) => void;
  /** Whether to show success toast (default: true) */
  showSuccessToast?: boolean;
  /** Whether to show error toast (default: true) */
  showErrorToast?: boolean;
}

/**
 * Hook result with mutation function and states
 */
export interface UseDeleteRoleResult {
  /** Mutation function to delete a role */
  deleteRole: (uuid: string) => Promise<void>;
  /** Whether the deletion is in progress */
  isLoading: boolean;
  /** Whether the deletion was successful */
  isSuccess: boolean;
  /** Whether the deletion failed */
  isError: boolean;
  /** Error object if deletion failed */
  error: Error | null;
  /** Reset mutation state */
  reset: () => void;
}

/**
 * React Query mutation hook for deleting a role
 *
 * Features:
 * - Automatic cache invalidation
 * - Success/error toast notifications
 * - Loading and error states
 * - TypeScript type safety
 *
 * @param options - Hook configuration options
 * @returns Mutation object with deleteRole function and states
 *
 * @example
 * const { deleteRole, isLoading } = useDeleteRole({
 *   onSuccess: (uuid) => console.log('Deleted:', uuid),
 *   showSuccessToast: true
 * });
 *
 * // With confirmation dialog
 * const handleDelete = async (uuid: string) => {
 *   if (window.confirm('¿Estás seguro de eliminar este rol?')) {
 *     await deleteRole(uuid);
 *   }
 * };
 */
export const useDeleteRole = (options: UseDeleteRoleOptions = {}): UseDeleteRoleResult => {
  const { onSuccess, onError, showSuccessToast = true, showErrorToast = true } = options;

  const { t } = useTranslation('roles');
  const queryClient = useQueryClient();
  const { handleError } = useApiErrorHandler();

  const mutation: UseMutationResult<void, Error, string> = useMutation({
    mutationFn: async (uuid: string) => {
      return getRoleUseCases().deleteRole.execute(uuid);
    },
    onSuccess: (_, uuid) => {
      // ✅ Invalidate roles list to refetch without deleted role
      queryClient.invalidateQueries(ROLES_QUERY_KEYS.list());

      // ✅ Remove specific role from cache
      queryClient.removeQueries(ROLES_QUERY_KEYS.detail(uuid));

      // ✅ Show success toast
      if (showSuccessToast) {
        toast.success(t('notifications.deleteSuccess'));
      }

      // ✅ Execute custom callback
      if (onSuccess) {
        onSuccess(uuid);
      }
    },
    onError: (error: Error, uuid: string) => {
      // ✅ Show error toast with context
      if (showErrorToast) {
        handleError(error, { actionContext: 'delete' });
      }

      // ✅ Execute custom callback
      if (onError) {
        onError(error, uuid);
      }
    },
  });

  return {
    deleteRole: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error || null,
    reset: mutation.reset,
  };
};
